import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import FluidButton from 'ui/FluidButton';
import TopNavigationBar from 'pureUi/TopNavigationBar';
import { useDispatch, useSelector } from 'react-redux';
import * as DashboardActions from 'store/modules/bookingManager/subdomains/dashboard/actions';
import * as DashboardSelectors from 'store/modules/bookingManager/subdomains/dashboard/selectors';
import { EUserType } from 'services/BackendApi';
import * as AuthSelectors from 'store/modules/auth/selectors';
import { makeBookingManagerApi } from 'services/BookingManagerApi';
import * as BookingManagerSelectors from 'store/modules/bookingManager/selectors';
import { useItineraryBreakdownQueries } from './useItineraryBreakdownQueries';
import { LoadingBar } from 'ui/NetworkStatusBar';
import { makeItineraryManagerApi } from 'services/ItineraryManagerApi';
import { ItineraryVersion } from 'services/ItineraryManagerApi/types';
import { Table } from 'ui/Table';
import { ESortOrder } from 'store/common/types';
import { formatDateDisplay } from 'utils';
import { useSorting } from 'hooks/useSorting';
import { CircleIconButton } from 'ui/CircleIconButton';
import { VerticalLine } from 'ui/VerticalLine';
import { theme } from '../../../tailwind.config';
import { Link } from 'ui/Link';
import { useTabFocus } from 'hooks/useTabFocus';
import * as BreakdownSelectors from 'store/modules/bookingManager/subdomains/breakdown/selectors';
import ChevronRightIcon from 'ui/Icons/tabler/chevron-right.component.svg';
const GreenDot = () => {
  return (
    <div
      style={{
        height: '12px',
        width: '12px',
        borderRadius: '50%',
        backgroundColor: theme['colors']['status-confirmed'],
      }}
    />
  );
};

const BlueDot = () => {
  return (
    <div
      style={{
        height: '12px',
        width: '12px',
        borderRadius: '50%',
        backgroundColor: theme['colors']['status-enquiry'],
      }}
    />
  );
};

const ItineraryVersionList = ({
  itineraryVersionList: initialItineraryVersionList,
  bookingUuid,
}: {
  itineraryVersionList: ItineraryVersion[];
  bookingUuid: string | undefined;
}) => {
  const sortingOps = useSorting('version', ESortOrder.DESC);

  let list = [...initialItineraryVersionList].sort((a, b) => {
    if (sortingOps.sortBy === 'version') {
      return sortingOps.sortOrder === ESortOrder.ASC ? a.version - b.version : b.version - a.version;
    }
    return 0;
  });

  if (list.length <= 0) {
    return (
      <span className="font-pt-sans text-black italic text-15px mb-20px">
        There are no itinerary views created yet.
      </span>
    );
  }

  return (
    <Table
      columns={[
        { id: 'version', label: 'Version', width: '70px', fieldToSortBy: 'version' },
        { id: 'updatedAt', label: 'Modified', width: '100px' },
        { id: 'updatedBy', label: 'Modified By', width: '125px' },
        { id: 'published', label: 'Published', width: '100px' },
        { id: 'publishedBy', label: 'Published By', width: '100px' },
        { id: 'link', label: 'Link', width: '180px' },
        { id: 'status', label: 'Status', width: '105px' },
        { id: 'actions', label: '', width: '55px' },
      ]}
      messageWhenEmpty="No results found"
      onSort={sortingOps.handleSort}
      sortBy={sortingOps.sortBy}
      sortOrder={sortingOps.sortOrder}
      rows={list.map(iv => {
        const relativeLink = `/itinerary/${bookingUuid}/${iv.version}`;
        return {
          id: iv.version.toString(),
          cells: [
            { id: 'version', value: iv.version.toString() },
            { id: 'updatedAt', value: formatDateDisplay(iv.updatedAt) },
            { id: 'updatedBy', value: iv.updatedBy },
            {
              id: 'published',
              value: iv.isPublished ? formatDateDisplay(iv.publishedDate) : '',
            },
            { id: 'publishedBy', value: iv.isPublished ? iv.publishedBy : '' },
            {
              id: 'link',
              className: 'text-ellipsis overflow-hidden whitespace-nowrap',
              value: (
                <a href={relativeLink} target="_blank">
                  {window.location.origin + relativeLink}
                </a>
              ),
            },
            {
              id: 'status',
              value: iv.isPublished ? (
                <span className="flex items-center gap-[5px] justify-between">
                  <span className="italic">Published</span>
                  <GreenDot />
                </span>
              ) : (
                <span className="flex items-center gap-[5px] justify-between">
                  <span className="italic">Not Published</span>
                  <BlueDot />
                </span>
              ),
            },

            {
              id: 'actions',
              value: (
                <span className="flex gap-3">
                  <VerticalLine borderWidth={2} height="30px" color={theme.colors['gray-18']} />
                  <Link to={relativeLink} target="_blank">
                    <CircleIconButton
                      type="secondary"
                      className="edit-action mx-1"
                      IconComponent={ChevronRightIcon}
                      onClick={() => {
                        // todo
                      }}
                    />
                  </Link>
                </span>
              ),
            },
          ],
        };
      })}
    ></Table>
  );
};

export const BookingManagerItinerary = () => {
  const { bookingUuid } = useParams<{ bookingUuid: string }>();
  const userRole = useSelector(AuthSelectors.getCurrentUserType) as EUserType;
  const isTa = useSelector(AuthSelectors.isTA);
  const topNavigationData = useSelector(DashboardSelectors.topNavigationDataDashboardSelector);
  const headlineLineItemBreakdown = useSelector(BreakdownSelectors.headlineLineItemBreakdownSelector);
  const bookingInformationReservationTeamData = useSelector(
    DashboardSelectors.bookingInformationReservationTeamDashboardSelector
  );
  const dispatch = useDispatch();
  const bookingManagerApi = makeBookingManagerApi();
  const itineraryManagerApi = makeItineraryManagerApi();

  const hasCreatePermissions = [EUserType.SR, EUserType.ADMIN, EUserType.FINANCE].includes(userRole);

  useEffect(() => {
    dispatch(DashboardActions.getTopNavigationDataRequestAction(bookingUuid));
  }, [bookingUuid]);

  const booking = useSelector(BookingManagerSelectors.bookingSelector);
  const itineraryQueries = useItineraryBreakdownQueries(bookingManagerApi, itineraryManagerApi, booking?.bookingUuid);

  useTabFocus(() => {
    itineraryQueries.invalidateGetItineraryVersionList();
  }, 3000);

  const isNetworkPending =
    itineraryQueries.getItineraryVersionList.isFetching || itineraryQueries.postGenerateItinerary.isPending;

  return (
    <>
      <TopNavigationBar
        userRole={userRole as EUserType}
        data={topNavigationData}
        headlineLineItemBreakdown={headlineLineItemBreakdown}
        bookingInformationReservationTeamData={bookingInformationReservationTeamData}
        isError={false}
      />

      <h1 className="text-4xl font-noe-display font-normal mt-0 mb-2">Itinerary</h1>

      {isNetworkPending ? (
        <>
          {itineraryQueries.getItineraryVersionList.isFetching && <LoadingBar label="Loading itinerary data..." />}
          {itineraryQueries.postGenerateItinerary.isPending && <LoadingBar label="Generating itinerary..." />}
        </>
      ) : null}

      {!isNetworkPending && (
        <>
          <ItineraryVersionList
            itineraryVersionList={itineraryQueries.getItineraryVersionList.data?.data ?? []}
            bookingUuid={booking?.bookingUuid}
          />

          {(itineraryQueries.getItineraryVersionList.data?.data.length || 0) <= 0 && hasCreatePermissions && (
            <FluidButton
              onClick={async () => {
                if (!booking) {
                  return;
                }
                itineraryQueries.postGenerateItinerary.mutate(booking.bookingUuid);
              }}
              className="w-[145px]"
              type="primary"
            >
              Create Itinerary
            </FluidButton>
          )}
        </>
      )}
    </>
  );
};
